import { useEffect, useRef, useState } from "react";

import { pricingService, type PriceQuote, type QuoteParams } from "../api/pricingService";
import { errorText } from "@/shared/lib/apiErrors";

/**
 * 底栏实时预估 —— `dryRun: true`,**只算不落库**。
 *
 * ★ 为什么不落库:用户在编辑期可能改十几次参数,每次落一行报价单是灾难,
 *   而且报价单 15 分钟有效期覆盖不了「慢慢调」的人。
 * ★ 防抖 300ms:参数是跟着输入框/滑块变的,不防抖会把报价口打成压测。
 * ★ 取不到价 → `credits` 为 null,底栏**不显示数字**(不兜旧值、不兜 0)。
 * ★ 竞态:只认最后一次请求的结果(`seq`)。参数快速切换时先发的请求可能后到,
 *   拿它覆盖新结果 = 底栏显示的是上一组参数的价钱,而用户已经改过了。
 */
export function useQuotePreview(
  params: QuoteParams | null,
  { debounceMs = 300 }: { debounceMs?: number } = {}
): { credits: number | null; loading: boolean } {
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const key = params ? `${params.skuCode}|${params.variantKey ?? ""}|${params.qty ?? 1}` : null;

  useEffect(() => {
    if (!params || !key) {
      setCredits(null);
      return;
    }
    const mine = ++seq.current;
    setLoading(true);
    const t = setTimeout(() => {
      pricingService
        .quote({ ...params, dryRun: true })
        .then((q) => {
          if (seq.current === mine) setCredits(q?.credits ?? null);
        })
        .catch(() => {
          // 报不出价就不显示数字 —— 不退回上一次的结果(参数已经变了)
          if (seq.current === mine) setCredits(null);
        })
        .finally(() => {
          if (seq.current === mine) setLoading(false);
        });
    }, debounceMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, debounceMs]);

  return { credits, loading };
}

/** 正式报价单的结果。`quoteId` 为空 = 还没拿到单 —— **没有单号就是没有单**。 */
export interface BindingQuote {
  quoteId: string | null;
  credits: number | null;
  /** 与底栏那个预估值不一致(编辑期间平台改了价)。只在不一致时为 true。 */
  priceChanged: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * 确认卡上那张**正式报价单** —— `dryRun: false`,落 `xb_price_quote` 并拿回单号。
 *
 * ★ **确认卡上显示的数必须来自这张单**,否则等于没解决问题:
 *   用户看的是 A、扣的是 B,而 A 是前端自己算的。
 * ★ 两者不一致(编辑期间平台改了价)时**正式那张单是权威**,而且要**明说一句
 *   「价格已更新」**:技术上我们没做错任何事,但用户会觉得数字被偷改 ——
 *   「报价即合同」保的是不偷扣,不是不解释。只在不一致时显示;每次都显示等于噪音,
 *   用户会学会无视它。
 * ★ 拿不到单就**不许提交**:没有单的任务结算时按单兑现会找不到单,
 *   而找不到单会静默回退到旧的成本 ×10 口径。
 *
 * @param previewCredits 底栏那个预估值(用来判断「价格已更新」);null = 不比
 */
export function useBindingQuote(
  params: QuoteParams | null,
  previewCredits: number | null
): BindingQuote {
  const [state, setState] = useState<Omit<BindingQuote, "loading" | "error">>({
    quoteId: null,
    credits: null,
    priceChanged: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = params ? `${params.skuCode}|${params.variantKey ?? ""}|${params.qty ?? 1}` : null;

  useEffect(() => {
    if (!params || !key) {
      setState({ quoteId: null, credits: null, priceChanged: false });
      setError(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    pricingService
      .quote({ ...params, dryRun: false })
      .then((q: PriceQuote) => {
        if (!alive) return;
        setState({
          quoteId: q?.quoteId ?? null,
          credits: q?.credits ?? null,
          priceChanged:
            previewCredits != null && q?.credits != null && q.credits !== previewCredits,
        });
      })
      .catch((e: unknown) => {
        if (!alive) return;
        // ★ 失败要说出来,不能静默让用户点「出图」——没有单的提交是这一批要根除的东西
        setState({ quoteId: null, credits: null, priceChanged: false });
        setError(errorText(e, { fallbackTitle: "报价失败" }));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // previewCredits 不进依赖:它只用来算 priceChanged,变了不该重新落一张单
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { ...state, loading, error };
}

/**
 * 提交那一刻取一张**正式报价单**,返回单号。给**没有确认卡**的线用(视频 / 数字人 / 长图)。
 *
 * ★ 和 {@link useBindingQuote} 的分工:那个是「打开确认卡就落单,卡上显示这张单的数」;
 *   这个是「点提交才落单」—— 因为这几条线**没有确认卡**,没有一个"用户正在看这个数"的时刻。
 *
 * ★ **报价失败不阻断提交,返回 null。** 这是有意的取舍,理由与确认卡那条同源:
 *   把「没有报价单」修成「根本不能创作」,后者的用户成本高一个量级。
 *   没有单的这一单会在后端落 `*_task_without_quote` WARN 并被验收判据②逮到 ——
 *   **它不会被忘掉**,但也不会把用户堵在门外。
 *
 * ⚠️ **已知缺口**:这几条线页面上显示的预估**仍不是这张单的数**(视频走
 *   `useVideoCreditEstimate`、数字人走 `estimateDhCost`)。绑单解决的是「结算按哪个价扣」,
 *   **没有**解决「用户看到的和扣的是不是同一个数」。后者要把这两个估算器也改成
 *   `dryRun` 报价 —— 那是下一节的事,别当成已经做完了。
 */
export async function acquireQuoteId(params: QuoteParams | null): Promise<string | null> {
  if (!params) {
    return null;
  }
  try {
    const q = await pricingService.quote({ ...params, dryRun: false });
    return q?.quoteId ?? null;
  } catch {
    return null;
  }
}
